import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { orderBy } from "lodash-es";
import { memo, useMemo, useState } from "react";
import { twJoin } from "tailwind-merge";

import type { ProjectOverviewItemDto, ProjectStatsDto } from "@/api/types";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { SegmentGroup } from "@/design-system/SegmentGroup";
import { Select } from "@/design-system/Select";
import { usePortfolioQueries } from "@/queries/portfolio/queryOptions";
import { useTranslation } from "@/translations";

import { PortfolioOverviewChart } from "./PortfolioOverviewChart";

interface Props {
  items: ProjectOverviewItemDto[];
  date: Date;
}
export const GraphView = memo(function GraphView({ items, date }: Props): React.ReactNode {
  const { t } = useTranslation();
  const [view, setView] = useState<"feelAtHome" | "rating" | "adoption" | "engagement">("feelAtHome");
  const [sort, setSort] = useState<"a-z" | "low-high" | "high-low">("a-z");
  const portfolioQueries = usePortfolioQueries();
  const { data: benchmark } = useQuery(
    portfolioQueries.benchmarkStates({
      date: format(date, "yyyy-MM-dd"),
    }),
  );
  const chartData = useMemo(() => {
    const statAccessor = (stats: ProjectStatsDto | undefined) => {
      switch (view) {
        case "adoption":
          return stats?.adoptionPercentage ?? -1;
        case "engagement":
          return stats?.engagedResidentPercentage ?? -1;
        case "rating":
          return stats?.averageTicketRating ?? -1;
        case "feelAtHome":
          return stats?.feelingAtHomeAverageRatingAllTime ?? -1;
        default:
          return -1;
      }
    };
    const orderedItems = orderBy(items, (item) => {
      if (sort === "a-z") {
        return item.name;
      }
      if (sort === "high-low") {
        return -statAccessor(item.stats);
      }

      return statAccessor(item.stats);
    });

    return {
      items: orderedItems.map((x) => ({
        x: x.name,
        y: statAccessor(x.stats) ?? 0,
      })),
      benchmark: orderedItems.map((x) => ({
        x: x.name,
        y: statAccessor(benchmark) ?? 0,
      })),
    };
  }, [benchmark, items, sort, view]);
  const options = [
    {
      label: t("page.portfolio.table.header.fah-rating"),
      value: "feelAtHome",
    },
    {
      label: t("page.portfolio.table.header.avg-rating"),
      value: "rating",
    },
    {
      label: t("page.portfolio.table.header.adoption"),
      value: "adoption",
    },
    {
      label: t("page.portfolio.table.header.engagement"),
      value: "engagement",
    },
  ] as const;

  let graphTitle = t("page.portfolio.analytics.graph-title.fah-rating");
  if (view === "adoption") {
    graphTitle = t("page.portfolio.analytics.graph-title.adoption");
  } else if (view === "engagement") {
    graphTitle = t("page.portfolio.analytics.graph-title.engagement");
  } else if (view === "rating") {
    graphTitle = t("page.portfolio.analytics.graph-title.avg-rating");
  }

  return (
    <section className="hidden flex-col gap-space-md @md:flex">
      <h2 className="text-body-emphasis">{t("page.portfolio.analytics.title")}</h2>
      <article className={twJoin("flex flex-col gap-space-sm px-space-lg py-space-md", cardSurfaceVariants())}>
        <span className="text-caption-emphasis">{graphTitle}</span>
        <header className="flex flex-col flex-wrap justify-between gap-y-space-sm @md:flex-row @md:items-center">
          <Inline gap="xs">
            <span className="text-caption">{t("page.portfolio.analytics.graph-subtitle")}</span>
            <span className="text-caption">
              <FormattedDate date={date} format="monthYear" />
            </span>
          </Inline>
          <Inline gap="sm">
            <SegmentGroup items={options} value={view} onChange={setView} />
          </Inline>
          <Inline gap="sm" align="center" wrap={false}>
            <span className="text-caption">{t("page.portfolio.analytics.sort")}</span>
            <Select
              items={["a-z", "high-low", "low-high"] as const}
              keySelector={(x) => x}
              renderOption={(x) => {
                if (x === "a-z") {
                  return t("page.portfolio.analytics.sort.type.a-z");
                }
                if (x === "high-low") {
                  return t("page.portfolio.analytics.sort.type.high-low");
                }

                return t("page.portfolio.analytics.sort.type.low-high");
              }}
              selected={sort}
              onChange={setSort}
            />
          </Inline>
        </header>
        <div className="relative pl-space-xl">
          <div className="absolute inset-y-0 left-0 my-auto size-0 -rotate-90">
            <p className="flex items-center justify-center text-caption whitespace-nowrap">
              {view === "feelAtHome" ? <span>{t("page.portfolio.table.header.fah-rating")} %</span> : null}
              {view === "engagement" ? <span>{t("page.portfolio.table.header.engagement")} %</span> : null}
              {view === "adoption" ? <span>{t("page.portfolio.table.header.adoption")} %</span> : null}
              {view === "rating" ? (
                <Inline gap="sm" align="center" wrap={false} as="span">
                  <span>{t("page.portfolio.table.header.avg-rating")}</span>
                  <Star01Icon appearance="solid" />
                </Inline>
              ) : null}
            </p>
          </div>
          <PortfolioOverviewChart
            view={view}
            month={date}
            dataPrimary={chartData.items}
            dataBenchmark={view === "rating" || view === "feelAtHome" ? chartData.benchmark : []}
            benchmarkLabel={t("page.portfolio.analytics.benchmark")}
            formatYTick={(x) => x.toString()}
            maxY={view === "rating" || view === "feelAtHome" ? 10 : 100}
          />
        </div>
        <p className="mt-space-sm text-center text-caption">{t("page.portfolio.analytics.bottom-title")}</p>
      </article>
      <div className="h-space-xl" />
    </section>
  );
});
